import React, { useMemo, useState } from 'react';
import Card from '../components/common/Card';
import ThreatLevelIndicator from '../components/common/ThreatLevelIndicator';
import Badge from '../components/common/Badge';
import { AlertCircle, ArrowRight, BarChart3, FileText, MapPin, Calendar, Users, Target, AlertTriangle, Filter, X, Download, Share2, Eye, Clock } from 'lucide-react';
import { nationalThreatLevel, lastUpdated, priorityAlerts, briefSnapshot, performanceMetrics } from '../utils/dummyData';
import { locationReports, bindaRegions, LocationReport } from '../utils/mapData';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
// Fix for default marker icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});
// Custom marker icons
const createCustomIcon = (riskLevel: string) => {
  const colors = {
    critical: '#DC2626',
    high: '#F97316',
    moderate: '#EAB308',
    low: '#22C55E'
  };
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        width: 20px;
        height: 20px;
        background-color: ${colors[riskLevel as keyof typeof colors]};
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      "></div>
    `,
    iconSize: [20, 20],
    iconAnchor: [10, 10]
  });
};
const Overview = () => {
  const [selectedReport, setSelectedReport] = useState<LocationReport | null>(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  // Filter states - standardized
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>('24h');
  const [selectedBinda, setSelectedBinda] = useState<string>('all');
  const [selectedRisk, setSelectedRisk] = useState<string>('all');
  const [selectedSource, setSelectedSource] = useState<string>('all');
  const [selectedDeputy, setSelectedDeputy] = useState<string>('all');
  // Filter the reports based on selected filters
  const filteredReports = useMemo(() => {
    return locationReports.filter(report => {
      const categoryMatch = selectedCategory === 'all' || report.category === selectedCategory;
      const bindaMatch = selectedBinda === 'all' || report.bindaRegion === selectedBinda;
      const riskMatch = selectedRisk === 'all' || report.riskLevel === selectedRisk;
      const sourceMatch = selectedSource === 'all' || report.sources.includes(selectedSource);
      return categoryMatch && bindaMatch && riskMatch && sourceMatch;
    });
  }, [selectedCategory, selectedBinda, selectedRisk, selectedSource]);
  const handleMarkerClick = (report: LocationReport) => {
    setSelectedReport(report);
    setShowDetailPanel(true);
  };
  const resetFilters = () => {
    setSelectedCategory('all');
    setSelectedTimeRange('24h');
    setSelectedBinda('all');
    setSelectedRisk('all');
    setSelectedSource('all');
  };
  const categories = ['Ideologi', 'Politik', 'Ekonomi', 'Sosial Budaya', 'Pertahanan Keamanan'];
  const sources = ['OSINT', 'HUMINT', 'SIGINT', 'IMINT', 'GEOINT', 'FININT', 'TECHINT', 'SOCMINT'];
  const deputies = ['Deputi I', 'Deputi II', 'Deputi III', 'Deputi IV', 'Deputi V', 'Deputi VI', 'Deputi VII', 'Deputi VIII', 'Deputi IX'];
  return <div className="space-y-6" data-id="element-615">
      <div className="flex items-center justify-between" data-id="element-616">
        <h1 className="text-2xl font-bold text-white" data-id="element-617">NEXUS BIN - Overview</h1>
        <div className="flex items-center space-x-3" data-id="element-618">
          <span className="text-sm text-gray-400" data-id="element-619">
            Last updated: 2 minutes ago
          </span>
          <div className="flex items-center px-2 py-1 bg-blue-900/30 rounded text-xs text-blue-400 border border-blue-800/50" data-id="element-620">
            <span data-id="element-621">ONLINE</span>
          </div>
        </div>
      </div>
      {/* Interactive Map with Filters */}
      <Card className="overflow-hidden" data-id="element-622">
        {/* Standardized Filter Bar */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 border-b border-gray-700 p-4" data-id="element-623">
          <div className="flex items-center justify-between mb-3" data-id="element-624">
            <div className="flex items-center space-x-2" data-id="element-625">
              <Filter className="h-5 w-5 text-cyan-400" data-id="element-626" />
              <span className="text-sm font-semibold text-white" data-id="element-627">
                INTELLIGENCE FILTERS
              </span>
            </div>
            <div className="flex items-center space-x-2" data-id="element-628">
              <span className="text-xs text-gray-400" data-id="element-629">
                {filteredReports.length} dari {locationReports.length} laporan
              </span>
              <button onClick={resetFilters} className="flex items-center px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded text-xs" data-id="element-630">
                <X className="h-3 w-3 mr-1" data-id="element-631" />
                Reset
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3" data-id="element-632">
            {/* Time Range Filter */}
            <div data-id="element-633">
              <label className="block text-xs text-gray-400 mb-1" data-id="element-634">
                Time Range
              </label>
              <select value={selectedTimeRange} onChange={e => setSelectedTimeRange(e.target.value)} className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500" data-id="element-635">
                <option value="1h" data-id="element-636">Last 1 Hour</option>
                <option value="6h" data-id="element-637">Last 6 Hours</option>
                <option value="24h" data-id="element-638">Last 24 Hours</option>
                <option value="7d" data-id="element-639">Last 7 Days</option>
                <option value="30d" data-id="element-640">Last 30 Days</option>
              </select>
            </div>
            {/* Category Filter */}
            <div data-id="element-641">
              <label className="block text-xs text-gray-400 mb-1" data-id="element-642">
                Category
              </label>
              <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500" data-id="element-643">
                <option value="all" data-id="element-644">All Categories</option>
                {categories.map(cat => <option key={cat} value={cat} data-id="element-645">
                    {cat}
                  </option>)}
              </select>
            </div>
            {/* Risk Level Filter */}
            <div data-id="element-646">
              <label className="block text-xs text-gray-400 mb-1" data-id="element-647">
                Risk Level
              </label>
              <select value={selectedRisk} onChange={e => setSelectedRisk(e.target.value)} className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500" data-id="element-648">
                <option value="all" data-id="element-649">All Levels</option>
                <option value="critical" data-id="element-650">Critical</option>
                <option value="high" data-id="element-651">High</option>
                <option value="moderate" data-id="element-652">Moderate</option>
                <option value="low" data-id="element-653">Low</option>
              </select>
            </div>
            {/* Deputy Filter */}
            <div data-id="element-654">
              <label className="block text-xs text-gray-400 mb-1" data-id="element-655">Deputy</label>
              <select value={selectedDeputy} onChange={e => setSelectedDeputy(e.target.value)} className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500" data-id="element-656">
                <option value="all" data-id="element-657">All Deputies</option>
                {deputies.map(dep => <option key={dep} value={dep} data-id="element-658">
                    {dep}
                  </option>)}
              </select>
            </div>
            {/* BINDA Filter */}
            <div data-id="element-659">
              <label className="block text-xs text-gray-400 mb-1" data-id="element-660">
                BINDA Region
              </label>
              <select value={selectedBinda} onChange={e => setSelectedBinda(e.target.value)} className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500" data-id="element-661">
                <option value="all" data-id="element-662">All BINDA</option>
                {bindaRegions.map(binda => <option key={binda} value={binda} data-id="element-663">
                    {binda}
                  </option>)}
              </select>
            </div>
            {/* Source Filter */}
            <div data-id="element-664">
              <label className="block text-xs text-gray-400 mb-1" data-id="element-665">
                Intel Source
              </label>
              <select value={selectedSource} onChange={e => setSelectedSource(e.target.value)} className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500" data-id="element-666">
                <option value="all" data-id="element-667">All Sources</option>
                {sources.map(source => <option key={source} value={source} data-id="element-668">
                    {source}
                  </option>)}
              </select>
            </div>
          </div>
        </div>
        {/* Map and Detail Panel Container */}
        <div className="flex" data-id="element-669">
          {/* Map Section */}
          <div className={`${showDetailPanel ? 'w-2/3' : 'w-full'} transition-all duration-300`} data-id="element-670">
            <div className="p-4" data-id="element-671">
              <div className="flex justify-between items-center mb-4" data-id="element-672">
                <h2 className="text-lg font-semibold text-white" data-id="element-673">
                  Peta Situasi Nasional
                </h2>
                <div className="flex items-center space-x-2" data-id="element-674">
                  <Badge variant="green" data-id="element-675">Live View</Badge>
                  <span className="text-xs text-gray-400" data-id="element-676">
                    {filteredReports.length} Laporan Aktif
                  </span>
                </div>
              </div>
              <div className="h-[600px] rounded-lg overflow-hidden border border-gray-800 bg-[#0a1628]" data-id="element-677">
                <MapContainer center={[-2.5489, 118.0149]} zoom={5} style={{
                height: '100%',
                width: '100%'
              }} className="z-0" data-id="element-678">
                  <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" data-id="element-679" />
                  {filteredReports.map(report => <Marker key={report.id} position={report.coordinates} icon={createCustomIcon(report.riskLevel)} eventHandlers={{
                  click: () => handleMarkerClick(report)
                }} data-id="element-680">
                      <Popup data-id="element-681">
                        <div className="p-2 min-w-[200px]" data-id="element-682">
                          <h3 className="font-semibold text-sm mb-1" data-id="element-683">
                            {report.title}
                          </h3>
                          <div className="space-y-1 text-xs text-gray-600" data-id="element-684">
                            <div className="flex items-center" data-id="element-685">
                              <MapPin className="h-3 w-3 mr-1" data-id="element-686" />
                              <span data-id="element-687">{report.location}</span>
                            </div>
                            <div className="flex items-center" data-id="element-688">
                              <span className="font-medium" data-id="element-689">Kategori:</span>
                              <span className="ml-1" data-id="element-690">{report.category}</span>
                            </div>
                            <div className="flex items-center" data-id="element-691">
                              <span className="font-medium" data-id="element-692">BINDA:</span>
                              <span className="ml-1" data-id="element-693">{report.bindaRegion}</span>
                            </div>
                          </div>
                          <div className="mt-2" data-id="element-694">
                            <Badge variant={report.riskLevel === 'critical' ? 'red' : report.riskLevel === 'high' ? 'orange' : report.riskLevel === 'moderate' ? 'yellow' : 'green'} data-id="element-695">
                              {report.riskLevel.toUpperCase()}
                            </Badge>
                          </div>
                        </div>
                      </Popup>
                    </Marker>)}
                </MapContainer>
              </div>
              <div className="flex justify-between mt-3 text-xs text-gray-400" data-id="element-696">
                <div className="flex space-x-4" data-id="element-697">
                  <div className="flex items-center" data-id="element-698">
                    <div className="w-3 h-3 rounded-full bg-green-500 mr-1" data-id="element-699"></div>
                    <span data-id="element-700">
                      Low (
                      {filteredReports.filter(r => r.riskLevel === 'low').length}
                      )
                    </span>
                  </div>
                  <div className="flex items-center" data-id="element-701">
                    <div className="w-3 h-3 rounded-full bg-yellow-500 mr-1" data-id="element-702"></div>
                    <span data-id="element-703">
                      Moderate (
                      {filteredReports.filter(r => r.riskLevel === 'moderate').length}
                      )
                    </span>
                  </div>
                  <div className="flex items-center" data-id="element-704">
                    <div className="w-3 h-3 rounded-full bg-orange-500 mr-1" data-id="element-705"></div>
                    <span data-id="element-706">
                      High (
                      {filteredReports.filter(r => r.riskLevel === 'high').length}
                      )
                    </span>
                  </div>
                  <div className="flex items-center" data-id="element-707">
                    <div className="w-3 h-3 rounded-full bg-red-600 mr-1" data-id="element-708"></div>
                    <span data-id="element-709">
                      Critical (
                      {filteredReports.filter(r => r.riskLevel === 'critical').length}
                      )
                    </span>
                  </div>
                </div>
                <span data-id="element-710">Klik marker untuk detail lengkap</span>
              </div>
            </div>
          </div>
          {/* Detail Panel */}
          {showDetailPanel && selectedReport && <div className="w-1/3 border-l border-gray-800 bg-gradient-to-b from-gray-900 to-gray-800 overflow-y-auto max-h-[700px]" data-id="element-711">
              <div className="p-4 border-b border-gray-700 flex justify-between items-center sticky top-0 bg-gray-900 z-10" data-id="element-712">
                <h3 className="text-lg font-semibold text-white" data-id="element-713">
                  Detail Laporan
                </h3>
                <button onClick={() => setShowDetailPanel(false)} className="p-1 hover:bg-gray-800 rounded" data-id="element-714">
                  <X className="h-5 w-5 text-gray-400" data-id="element-715" />
                </button>
              </div>
              <div className="p-4 space-y-4" data-id="element-716">
                {/* Header */}
                <div data-id="element-717">
                  <h4 className="text-xl font-bold text-white mb-2" data-id="element-718">
                    {selectedReport.title}
                  </h4>
                  <div className="flex items-center space-x-2 mb-3" data-id="element-719">
                    <Badge variant={selectedReport.riskLevel === 'critical' ? 'red' : selectedReport.riskLevel === 'high' ? 'orange' : selectedReport.riskLevel === 'moderate' ? 'yellow' : 'green'} data-id="element-720">
                      {selectedReport.riskLevel.toUpperCase()}
                    </Badge>
                    <Badge variant="blue" data-id="element-721">{selectedReport.category}</Badge>
                    <Badge variant="purple" data-id="element-722">{selectedReport.status}</Badge>
                  </div>
                </div>
                {/* AI Summary */}
                <div className="bg-gradient-to-r from-cyan-900/20 to-blue-900/20 border border-cyan-800/30 rounded-lg p-3" data-id="element-723">
                  <div className="flex items-center mb-2" data-id="element-724">
                    <div className="w-2 h-2 rounded-full bg-cyan-400 mr-2 animate-pulse" data-id="element-725"></div>
                    <span className="text-xs font-semibold text-cyan-400 uppercase" data-id="element-726">
                      AI Intelligence Summary
                    </span>
                  </div>
                  <p className="text-sm text-gray-300 leading-relaxed" data-id="element-727">
                    {selectedReport.aiSummary}
                  </p>
                </div>
                {/* Key Information */}
                <div className="grid grid-cols-2 gap-3" data-id="element-728">
                  <div className="bg-gray-800/50 rounded-lg p-3" data-id="element-729">
                    <p className="text-xs text-gray-400 mb-1" data-id="element-730">Lokasi</p>
                    <p className="text-sm text-white font-medium" data-id="element-731">
                      {selectedReport.location}
                    </p>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-3" data-id="element-732">
                    <p className="text-xs text-gray-400 mb-1" data-id="element-733">BINDA</p>
                    <p className="text-sm text-white font-medium" data-id="element-734">
                      {selectedReport.bindaRegion}
                    </p>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-3" data-id="element-735">
                    <p className="text-xs text-gray-400 mb-1" data-id="element-736">Deputi</p>
                    <p className="text-sm text-white font-medium" data-id="element-737">
                      {selectedReport.deputy}
                    </p>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-3" data-id="element-738">
                    <p className="text-xs text-gray-400 mb-1" data-id="element-739">Waktu</p>
                    <p className="text-sm text-white font-medium" data-id="element-740">
                      {selectedReport.timestamp}
                    </p>
                  </div>
                </div>
                {/* Description */}
                <div data-id="element-741">
                  <h5 className="text-sm font-semibold text-white mb-2" data-id="element-742">
                    Deskripsi
                  </h5>
                  <p className="text-sm text-gray-300 leading-relaxed" data-id="element-743">
                    {selectedReport.description}
                  </p>
                </div>
                {/* Affected Area */}
                <div data-id="element-744">
                  <h5 className="text-sm font-semibold text-white mb-2" data-id="element-745">
                    Area Terdampak
                  </h5>
                  <p className="text-sm text-gray-300" data-id="element-746">
                    {selectedReport.affectedArea}
                  </p>
                </div>
                {/* Impact */}
                <div className="bg-orange-900/20 border border-orange-800/30 rounded-lg p-3" data-id="element-747">
                  <h5 className="text-sm font-semibold text-orange-400 mb-2" data-id="element-748">
                    Estimasi Dampak
                  </h5>
                  <p className="text-sm text-gray-300" data-id="element-749">
                    {selectedReport.estimatedImpact}
                  </p>
                </div>
                {/* Recommendations */}
                <div data-id="element-750">
                  <h5 className="text-sm font-semibold text-white mb-2" data-id="element-751">
                    Rekomendasi Tindakan
                  </h5>
                  <ul className="space-y-2" data-id="element-752">
                    {selectedReport.recommendations.map((rec, index) => <li key={index} className="flex items-start" data-id="element-753">
                        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-cyan-600 text-xs text-white mr-2 mt-0.5 flex-shrink-0" data-id="element-754">
                          {index + 1}
                        </span>
                        <span className="text-sm text-gray-300" data-id="element-755">{rec}</span>
                      </li>)}
                  </ul>
                </div>
                {/* Sources */}
                <div data-id="element-756">
                  <h5 className="text-sm font-semibold text-white mb-2" data-id="element-757">
                    Sumber Intelijen
                  </h5>
                  <div className="flex flex-wrap gap-2" data-id="element-758">
                    {selectedReport.sources.map(source => <span key={source} className="px-3 py-1 bg-gray-800 text-cyan-400 text-xs rounded-full border border-gray-700" data-id="element-759">
                        {source}
                      </span>)}
                  </div>
                </div>
                {/* Related Cases */}
                {selectedReport.relatedCases.length > 0 && <div data-id="element-760">
                    <h5 className="text-sm font-semibold text-white mb-2" data-id="element-761">
                      Kasus Terkait
                    </h5>
                    <div className="flex flex-wrap gap-2" data-id="element-762">
                      {selectedReport.relatedCases.map(caseId => <span key={caseId} className="px-3 py-1 bg-gray-800 text-yellow-400 text-xs rounded-full border border-gray-700" data-id="element-763">
                          {caseId}
                        </span>)}
                    </div>
                  </div>}
                {/* Action Buttons */}
                <div className="flex flex-col space-y-2 pt-4 border-t border-gray-700" data-id="element-764">
                  <button className="w-full flex items-center justify-center px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm font-medium" data-id="element-765">
                    <Eye className="h-4 w-4 mr-2" data-id="element-766" />
                    Buka Kasus Lengkap
                  </button>
                  <div className="grid grid-cols-2 gap-2" data-id="element-767">
                    <button className="flex items-center justify-center px-3 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm" data-id="element-768">
                      <Download className="h-4 w-4 mr-1" data-id="element-769" />
                      Export
                    </button>
                    <button className="flex items-center justify-center px-3 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm" data-id="element-770">
                      <Share2 className="h-4 w-4 mr-1" data-id="element-771" />
                      Share
                    </button>
                  </div>
                </div>
              </div>
            </div>}
        </div>
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-772">
        {/* National Threat Level */}
        <Card className="p-4" data-id="element-773">
          <div className="flex justify-between items-start mb-4" data-id="element-774">
            <h2 className="text-lg font-semibold text-white" data-id="element-775">
              National Threat Level
            </h2>
            <Badge variant="blue" data-id="element-776">Real-time</Badge>
          </div>
          <div className="flex flex-col items-center justify-center py-6" data-id="element-777">
            <div className="w-32 h-32 rounded-full border-8 border-yellow-500 flex items-center justify-center mb-4" data-id="element-778">
              <ThreatLevelIndicator level={nationalThreatLevel} data-id="element-779" />
            </div>
            <p className="text-sm text-gray-400" data-id="element-780">Berdasarkan {lastUpdated}</p>
          </div>
        </Card>
        {/* Top 5 Priority Alerts */}
        <Card className="flex flex-col" data-id="element-781">
          <div className="p-4 border-b border-gray-800" data-id="element-782">
            <div className="flex justify-between items-center" data-id="element-783">
              <h2 className="text-lg font-semibold text-white" data-id="element-784">
                Top 5 Priority Alerts
              </h2>
              <AlertCircle className="h-5 w-5 text-red-500" data-id="element-785" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto" data-id="element-786">
            <ul className="divide-y divide-gray-800" data-id="element-787">
              {priorityAlerts.map(alert => <li key={alert.id} className="p-3 hover:bg-gray-800/50 cursor-pointer" data-id="element-788">
                  <div className="flex justify-between" data-id="element-789">
                    <p className="text-sm font-medium text-white" data-id="element-790">
                      {alert.title}
                    </p>
                    <Badge variant={alert.riskLevel === 'critical' ? 'red' : alert.riskLevel === 'high' ? 'orange' : alert.riskLevel === 'moderate' ? 'yellow' : 'green'} data-id="element-791">
                      {alert.riskLevel}
                    </Badge>
                  </div>
                  <div className="flex items-center mt-1 text-xs text-gray-400" data-id="element-792">
                    <MapPin className="h-3 w-3 mr-1" data-id="element-793" />
                    <span data-id="element-794">{alert.location}</span>
                    <span className="mx-2" data-id="element-795">•</span>
                    <span data-id="element-796">{alert.deputy}</span>
                  </div>
                </li>)}
            </ul>
          </div>
          <div className="p-3 border-t border-gray-800" data-id="element-797">
            <button className="w-full py-2 flex items-center justify-center text-sm text-cyan-400 hover:text-cyan-300" data-id="element-798">
              View All Alerts
              <ArrowRight className="h-4 w-4 ml-1" data-id="element-799" />
            </button>
          </div>
        </Card>
        {/* Today's iBRIEF Snapshot */}
        <Card className="flex flex-col" data-id="element-800">
          <div className="p-4 border-b border-gray-800" data-id="element-801">
            <div className="flex justify-between items-center" data-id="element-802">
              <h2 className="text-lg font-semibold text-white" data-id="element-803">
                Today's iBRIEF Snapshot
              </h2>
              <FileText className="h-5 w-5 text-cyan-500" data-id="element-804" />
            </div>
          </div>
          <div className="p-4 flex-1" data-id="element-805">
            <ul className="space-y-3" data-id="element-806">
              {briefSnapshot.map((item, index) => <li key={index} className="flex items-start" data-id="element-807">
                  <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-gray-700 text-xs text-white mr-3 mt-0.5" data-id="element-808">
                    {index + 1}
                  </span>
                  <p className="text-sm text-gray-300" data-id="element-809">{item}</p>
                </li>)}
            </ul>
          </div>
          <div className="p-3 border-t border-gray-800" data-id="element-810">
            <button className="w-full py-2 flex items-center justify-center text-sm text-cyan-400 hover:text-cyan-300" data-id="element-811">
              View Complete iBRIEF
              <ArrowRight className="h-4 w-4 ml-1" data-id="element-812" />
            </button>
          </div>
        </Card>
      </div>
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-813">
        <Card className="p-4" data-id="element-814">
          <div className="flex items-center justify-between" data-id="element-815">
            <div className="flex items-center" data-id="element-816">
              <AlertTriangle className="h-5 w-5 text-red-500 mr-3" data-id="element-817" />
              <span className="text-sm text-gray-300" data-id="element-818">Active Threats</span>
            </div>
            <span className="text-2xl font-bold text-white" data-id="element-819">
              {locationReports.length}
            </span>
          </div>
        </Card>
        <Card className="p-4" data-id="element-820">
          <div className="flex items-center justify-between" data-id="element-821">
            <div className="flex items-center" data-id="element-822">
              <Target className="h-5 w-5 text-orange-500 mr-3" data-id="element-823" />
              <span className="text-sm text-gray-300" data-id="element-824">High Priority</span>
            </div>
            <span className="text-2xl font-bold text-white" data-id="element-825">
              {locationReports.filter(r => r.riskLevel === 'critical' || r.riskLevel === 'high').length}
            </span>
          </div>
        </Card>
        <Card className="p-4" data-id="element-826">
          <div className="flex items-center justify-between" data-id="element-827">
            <div className="flex items-center" data-id="element-828">
              <Users className="h-5 w-5 text-cyan-500 mr-3" data-id="element-829" />
              <span className="text-sm text-gray-300" data-id="element-830">BINDA Reporting</span>
            </div>
            <span className="text-2xl font-bold text-white" data-id="element-831">
              {bindaRegions.length}
            </span>
          </div>
        </Card>
        <Card className="p-4" data-id="element-832">
          <div className="flex items-center justify-between" data-id="element-833">
            <div className="flex items-center" data-id="element-834">
              <Clock className="h-5 w-5 text-green-500 mr-3" data-id="element-835" />
              <span className="text-sm text-gray-300" data-id="element-836">Reports Today</span>
            </div>
            <span className="text-2xl font-bold text-white" data-id="element-837">28</span>
          </div>
        </Card>
      </div>
    </div>;
};
export default Overview;